import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useMaxUiKpis } from "../common/hooks/useMaxUiKpis";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { ActionReportsTreeView } from "../common/ActionReportTreeView/ActionReportsTreeView";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { FetchNbbvActionReportData } from "./NbbvActionReportDataLayer";
import { setAvailableKpis, setAffAbosToExpand } from "../../../reducers/nbbvArReport";
import { useActionReportTreeView } from "../common/hooks/useActionReportTreeView";
import defaultContent from "../../../content/actionReportsNbbv";
import { MODULE_NON_BONUSABLE_BV_V2 } from "../../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import {
  nbbvPageViewTrack,
  nbbvExportTrack,
  nbbvCustomizeKpiTrack,
  nbbvCancelCustomizeKpiTrack,
  nbbvApplyCustomizeKpiTrack,
  nbbvProfileTrack,
  nbbvAddToWatchlistTrack,
  nbbvRemoveFromWatchlistTrack,
  nbbvPhoneIconTrack,
  nbbvMessageIconTrack,
} from "../../../common/tealium/ActionReports/NbbvTrack";
import "../ActionReports.scss";
import "./NbbvActionReport.scss";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";

const DEFAULT_KPIS = [KPI_ID.NON_BONUSABLE_BV, KPI_ID.VCS_PERCENT, KPI_ID.PERSONAL_PV, KPI_ID.VCS_PV];

/**
 * Creates field configuration for KPI columns in the action report table.
 * @param kpiIds - Array of KPI identifiers from KPI_ID constants
 * @param tableColumns - Translated column labels keyed by KPI ID
 * @returns Array of field configs where:
 *   - id: Used by AboEnrichment API to fetch KPI data and by export to map fields
 *   - kpi: Used by ActionReportListView to match columns with row data
 *   - title: Displayed as column header in the table
 */
const createFieldConfig = (kpiIds: string[], tableColumns: { [key: string]: string }) => [
  ...kpiIds.map((kpiId) => ({
    id: kpiId,
    kpi: kpiId,
    title: tableColumns[kpiId] || kpiId,
  })),
  { id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" },
  { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
];

const useSelectedKpis = (configuredKpis: string[]) => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  useGetSavedKpiPreferenceQuery("nbbvKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(() => {
    const savedView = savedKpiPreferencesData?.savedDataViews?.nbbvKPIs;
    if (savedView) {
      return savedView.availableKpis
        .filter((kpi: { displaydefault: boolean; id: string }) => kpi.displaydefault && configuredKpis.includes(kpi.id))
        .map((kpi: { id: string }) => kpi.id);
    }
    return DEFAULT_KPIS.filter((kpi) => configuredKpis.includes(kpi));
  }, [savedKpiPreferencesData, configuredKpis]);
};

export default function NbbvActionReport() {
  const { t } = useTranslation(["actionReportsNbbv", "leafAboName"]);
  const maxUiKpis = useMaxUiKpis();
  const dispatch = useAppDispatch();
  const { configuration } = useAppSelector(({ boot }) => boot);
  const { uiKpis } = configuration;
  const { availableKpis } = useAppSelector((state) => state.nbbvActionReportTracker);
  const confidentialUserLabel = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });

  const { selectedPeriod } = useAppSelector((state) => state.period);

  const nbbvV2 = uiKpis?.nbbvV2 || DEFAULT_KPIS;

  const { nbbvData, isLoading, errorStatus } = FetchNbbvActionReportData(selectedPeriod);

  const hasData = !isLoading && !errorStatus && (nbbvData.downlines || []).length > 0;

  const allAbos = useMemo(
    () => (nbbvData.downlines || []).map((d) => ({ aff: d.aff, abo: d.abo })),
    [nbbvData.downlines],
  );

  const { isTreeView, handleToggle } = useActionReportTreeView({
    isLoading,
    hasData,
    setAffAbosToExpand,
    allAbos,
  });

  // Search state
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    dispatch(setAvailableKpis(nbbvV2));
  }, [dispatch, nbbvV2]);

  useEffect(() => {
    nbbvPageViewTrack(selectedPeriod);
  }, [selectedPeriod]);

  const handleWatchlistTrack = (isAdding: boolean) => {
    if (isAdding) {
      nbbvAddToWatchlistTrack();
    } else {
      nbbvRemoveFromWatchlistTrack();
    }
  };

  const tableColumns = t("table.columns", {
    ns: "actionReportsNbbv",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as { [key: string]: string };

  const selectedKpis = useSelectedKpis(nbbvV2);
  const fieldConfig = useMemo(() => createFieldConfig(nbbvV2, tableColumns), [nbbvV2, tableColumns]);

  const reportData = useMemo(() => {
    return (nbbvData.downlines || []).map((d) => ({
      aboName: d.confidentialUser ? confidentialUserLabel : d.aboName,
      aboNumber: d.abo,
      abo: d.abo,
      aff: d.aff,
      level: d.level,
      isoCountry: d.isoCountry,
      imageRefUrl: d.imageRefUrl,
      // Pre-formatted KPI values from NBBV API — displayed immediately while enrichment fetches phoneNumber
      [KPI_ID.NON_BONUSABLE_BV]: d.nonBonusableBV?.formatted ?? "--",
      [KPI_ID.VCS_PERCENT]: d.personalVcsPercent?.formatted ?? "--",
      [KPI_ID.PERSONAL_PV]: d.personalPV?.formatted ?? "--",
      [KPI_ID.VCS_PV]: d.vcsPV?.formatted ?? "--",
    }));
  }, [nbbvData.downlines, confidentialUserLabel]);

  const totalAboCount = reportData.length;

  // Search pool for filtering
  type NbbvSearchEntry = {
    abo: string;
    aff: string;
    isoCountry: string;
    aboName: string;
    index: number;
  };

  const searchPool = useMemo<NbbvSearchEntry[]>(() => {
    return (nbbvData.downlines || []).map((d, index) => ({
      abo: d.abo,
      aff: d.aff,
      isoCountry: d.isoCountry,
      aboName: d.confidentialUser ? confidentialUserLabel : d.aboName,
      index,
    }));
  }, [nbbvData.downlines, confidentialUserLabel]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `${entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}`,
            label: entry.aboName,
            sublabel,
          };
        }),
    [searchPool],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}` === key);
      if (found) {
        setScrollToIndex(found.index);
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToIndex(undefined);
  }, []);

  const exportAffAbos = useMemo(() => {
    return reportData.map((record) => ({
      affAbo: `${record.aff}-${record.abo}`,
    }));
  }, [reportData]);

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_NON_BONUSABLE_BV_V2]: t("export.exportTypeLabel", defaultContent.export.exportTypeLabel).toString(),
    }),
    [t],
  );

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  const renderContent = () => {
    if (isTreeView) {
      return <ActionReportsTreeView path="nbbv" />;
    }

    if (isLoading) {
      return <ActionReportListViewLoader />;
    }

    if (errorStatus) {
      return (
        <div className="NbbvAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    if (totalAboCount === 0) {
      return (
        <div className="NbbvAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    return (
      <div className="NbbvAR__listViewWrapper">
        <ActionReportListView
          selectedKpis={selectedKpis.filter((kpi) => availableKpis.includes(kpi))}
          reportId="nbbvAR"
          reportData={reportData}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          scrollToIndex={scrollToIndex}
          onProfileTrack={nbbvProfileTrack}
          onWatchlistTrack={handleWatchlistTrack}
          onPhoneTrack={nbbvPhoneIconTrack}
          onMessageTrack={nbbvMessageIconTrack}
          showImageBlock={true}
        />
      </div>
    );
  };

  const pageTitle = getCanonicalTitle({ id: "nonBonusableBv" }, t, {});

  return (
    <main className="NbbvAR">
      <ActionReportHeader
        title={pageTitle}
        description={
          <span className="NbbvAR__description">
            <span className="NbbvAR__count">
              {isLoading ? (
                <LeafRectangleLoader isLoading={true} width="2rem" height="0.75rem" margin="0" />
              ) : (
                <strong>{totalAboCount}</strong>
              )}
            </span>{" "}
            {t("title.pageDescription", defaultContent.title.pageDescription).toString()}
          </span>
        }
        reportLength={totalAboCount}
      />
      <ActionReportToolbar
        reportLength={totalAboCount}
        isAllEnriched={true}
        treeViewToggle={{
          enabled: true,
          isTreeView: isTreeView,
          onToggle: handleToggle,
          disabled: !hasData,
          label: t("losTreeView", defaultContent.losTreeView) as string,
        }}
        kpiSelector={
          !isTreeView
            ? {
                availableKpis: nbbvV2,
                defaultKpis: DEFAULT_KPIS,
                saveViewName: "nbbvKPIs",
                saveViewDescription: "NBBV KPI Preferences",
                maxSelectionsAllowed: maxUiKpis,
                onOpenTrack: nbbvCustomizeKpiTrack,
                onCancelTrack: nbbvCancelCustomizeKpiTrack,
                onApplyTrack: nbbvApplyCustomizeKpiTrack,
                disabled: isLoading || totalAboCount === 0,
              }
            : undefined
        }
        search={
          !isTreeView
            ? {
                enabled: true,
                searchCallback: handleSearchCallback,
                selectionCallback: handleSelectionCallback,
                searchText: searchText,
                clearFunction: handleClearSearch,
                disabled: isLoading || searchPool.length === 0,
              }
            : undefined
        }
        export={{
          moduleId: MODULE_NON_BONUSABLE_BV_V2,
          exportAffAbos: exportAffAbos,
          selectedKpis: selectedKpis.filter((kpi) => nbbvV2.includes(kpi)),
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_NON_BONUSABLE_BV_V2,
          startedByLabels,
          fieldTitleOverrides,
        }}
        exportTrack={nbbvExportTrack}
      />
      <div className="NbbvAR__content">{renderContent()}</div>
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_NON_BONUSABLE_BV_V2}
        notifyDownloadLinkOnClick={() => dispatchOpenExportV2ModalEvent(MODULE_NON_BONUSABLE_BV_V2, true)}
      />
    </main>
  );
}
